/**
 * Closed candidate set for queue-minted standing-rule match text.
 *
 * Lockstep with `pipeline/match_text.py` and `ingest-worker/src/match_text.ts`
 * is pinned by `tests/test_match_text_lockstep.py` — not by this comment.
 *
 * Tokenisation whitespace is the union of Python `\s` and JS `\s`.
 * Purely numeric means ASCII `[0-9]` (store numbers). ASCII bank
 * descriptors: `toLowerCase` matches Python `str.lower`.
 */

export const MATCH_TEXT_MAX_LENGTH = 500;

function isPurelyNumeric(text: string): boolean {
  const compact = text.replace(/[ \t]/g, "");
  return compact.length > 0 && /^[0-9]+$/.test(compact);
}

export function matchTextCandidates(
  descriptor: string | null | undefined,
  merchant: string | null | undefined,
): string[] {
  const seen = new Map<string, string>();
  const desc = typeof descriptor === "string" ? descriptor : "";
  // JS `\s` already includes U+FEFF; add the Python-only `\s` code points.
  // eslint-disable-next-line no-control-regex -- engine `\s` lockstep (R32A-4)
  const tokenRe = /[^\s\u001c\u001d\u001e\u001f\u0085]+/g;
  const tokens: Array<{ start: number; end: number }> = [];
  let match: RegExpExecArray | null = tokenRe.exec(desc);
  while (match !== null) {
    tokens.push({ start: match.index, end: match.index + match[0].length });
    match = tokenRe.exec(desc);
  }
  const add = (span: string): void => {
    const key = span.toLowerCase();
    if (key.length > 0 && !seen.has(key)) seen.set(key, span);
  };
  if (tokens.length > 0) {
    const first = tokens[0];
    const last = tokens[tokens.length - 1];
    if (first === undefined || last === undefined) {
      return [...seen.values()];
    }
    const full = desc.slice(first.start, last.end);
    for (let i = 0; i < tokens.length; i += 1) {
      const startTok = tokens[i];
      if (startTok === undefined) continue;
      for (let j = i; j < tokens.length; j += 1) {
        const endTok = tokens[j];
        if (endTok === undefined) continue;
        const span = desc.slice(startTok.start, endTok.end);
        if (isPurelyNumeric(span) && span !== full) continue;
        add(span);
      }
    }
  }
  const merchantText = (merchant ?? "").trim();
  if (merchantText.length > 0) {
    if (desc.length > 0) {
      const idx = desc.toLowerCase().indexOf(merchantText.toLowerCase());
      if (idx >= 0) add(desc.slice(idx, idx + merchantText.length));
    } else if (tokens.length === 0) {
      add(merchantText);
    }
  }
  return [...seen.values()];
}

export function matchTextIsCandidate(
  matchText: string,
  descriptor: string | null | undefined,
  merchant: string | null | undefined,
): boolean {
  return matchTextCandidates(descriptor, merchant).includes(matchText);
}
